import * as React from "react";

import { type Device } from "@/shared/domain/devices";
import { useAllDevicesByOrg } from "@/shared/domain/devices/hooks/useAllDevicesByOrg";
import { EntityIcon } from "@/shared/foundation/icons";
import {
  Banner,
  Combobox,
  FormSection,
  Popover,
  Tag,
} from "@/shared/foundation/primitives";

import styles from "./CreateSessionPage.module.css";

interface DevicesSectionProps {
  orgId: string;
  inferredDeviceIds: string[];
  isInferringDevices: boolean;
  inferDevicesError: Error | null;
  explicitDeviceIds: string[];
  onAddDevice: (deviceId: string) => void;
  onRemoveDevice: (deviceId: string) => void;
}

/**
 * Devices inferred from the selected files are display-only — the backend
 * links them to the session automatically. Devices the user adds here are
 * sent explicitly in the create request.
 */
export function DevicesSection({
  orgId,
  inferredDeviceIds,
  isInferringDevices,
  inferDevicesError,
  explicitDeviceIds,
  onAddDevice,
  onRemoveDevice,
}: DevicesSectionProps) {
  // Removing a tag unmounts the button that had focus; hand it to the add
  // trigger so a keyboard user keeps their place in the row.
  const addTriggerRef = React.useRef<HTMLButtonElement>(null);
  const handleRemove = (deviceId: string) => {
    onRemoveDevice(deviceId);
    addTriggerRef.current?.focus();
  };

  return (
    <FormSection
      title="Devices"
      hint="Devices on the selected files are linked automatically."
    >
      <InferenceStatus
        hasDevices={inferredDeviceIds.length > 0}
        isLoading={isInferringDevices}
        error={inferDevicesError}
      />
      <div className={styles.deviceRow}>
        <InferredDeviceTags deviceIds={inferredDeviceIds} />
        <ExplicitDeviceTags
          deviceIds={explicitDeviceIds}
          onRemove={handleRemove}
        />
        <AddDevicePopover
          orgId={orgId}
          linkedDeviceIds={[...inferredDeviceIds, ...explicitDeviceIds]}
          onAddDevice={onAddDevice}
          triggerRef={addTriggerRef}
        />
      </div>
    </FormSection>
  );
}

function InferenceStatus({
  hasDevices,
  isLoading,
  error,
}: {
  hasDevices: boolean;
  isLoading: boolean;
  error: Error | null;
}) {
  if (error !== null) {
    return (
      <Banner
        variant="error"
        text={`Failed to infer devices from the selected files: ${error.message}`}
      />
    );
  }
  // While a first inference is in flight there are no held device ids yet;
  // say so rather than rendering nothing. A refetch keeps showing the held
  // previous result instead.
  if (isLoading && !hasDevices) {
    return (
      <p className={styles.muted}>Finding devices from the selected files…</p>
    );
  }
  return null;
}

function InferredDeviceTags({ deviceIds }: { deviceIds: string[] }) {
  if (deviceIds.length === 0) {
    return null;
  }
  return (
    <>
      <ul className={styles.tagList} aria-label="Inferred devices">
        {deviceIds.map((deviceId) => (
          <li key={deviceId}>
            <DeviceTag deviceId={deviceId} />
          </li>
        ))}
      </ul>
      <span className={styles.tagNote}>from files</span>
    </>
  );
}

function ExplicitDeviceTags({
  deviceIds,
  onRemove,
}: {
  deviceIds: string[];
  onRemove: (deviceId: string) => void;
}) {
  if (deviceIds.length === 0) {
    return null;
  }
  return (
    <ul className={styles.tagList} aria-label="Attached devices">
      {deviceIds.map((deviceId) => (
        <li key={deviceId}>
          <DeviceTag deviceId={deviceId} onDelete={() => onRemove(deviceId)} />
        </li>
      ))}
    </ul>
  );
}

function DeviceTag({
  deviceId,
  onDelete,
}: {
  deviceId: string;
  onDelete?: () => void;
}) {
  return (
    <Tag
      label={deviceId}
      icon={<EntityIcon type="device" size={12} />}
      size="compact"
      onDelete={onDelete}
    />
  );
}

/**
 * The dashed "+ Device" trigger. Picking a device in its popover adds it at
 * once and closes the popover. The picker lists only the org's devices not
 * already linked, so what it adds always names a device.
 */
function AddDevicePopover({
  orgId,
  linkedDeviceIds,
  onAddDevice,
  triggerRef,
}: {
  orgId: string;
  linkedDeviceIds: string[];
  onAddDevice: (deviceId: string) => void;
  triggerRef: React.Ref<HTMLButtonElement>;
}) {
  const [open, setOpen] = React.useState(false);
  const devicesQuery = useAllDevicesByOrg(orgId);
  const linked = new Set(linkedDeviceIds);
  const offered = (devicesQuery.data ?? []).filter(
    (device) => !linked.has(device.device_id),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger ref={triggerRef} className={styles.addDevice}>
        + Device
      </Popover.Trigger>
      <Popover.Content
        aria-label="Add a device"
        className={styles.devicePicker}
      >
        <Combobox<Device>
          items={offered}
          value={null}
          onValueChange={(device) => {
            if (device !== null) {
              onAddDevice(device.device_id);
              setOpen(false);
            }
          }}
          itemToStringLabel={(device) => device.device_id}
          isItemEqualToValue={(item, value) =>
            item.device_id === value.device_id
          }
          // Disabled until the list loads, so an empty list never reads as
          // "No matches"; the placeholder says why.
          disabled={!devicesQuery.isSuccess || offered.length === 0}
          label="Device"
          autoHighlight
          size="sm"
        >
          <Combobox.TriggerInput
            placeholder={pickerPlaceholder(devicesQuery, offered)}
          />
          <Combobox.Content>
            <Combobox.Empty />
            <Combobox.List>
              {(device: Device) => (
                <Combobox.Item key={device.device_id} value={device} />
              )}
            </Combobox.List>
          </Combobox.Content>
        </Combobox>
      </Popover.Content>
    </Popover>
  );
}

function pickerPlaceholder(
  query: ReturnType<typeof useAllDevicesByOrg>,
  offered: readonly Device[],
): string {
  if (query.isPending) {
    return "Loading devices…";
  }
  if (query.isError) {
    return "Couldn’t load devices";
  }
  return offered.length === 0 ? "No more devices to add" : "Search devices";
}
